/*
==========================================
STICKER IMAGE PROCESSOR
版本: v1.1.0
文件: assets/sticker-image-processor.js
说明: 贴纸专用照片处理，4种风格

风格列表:
  color-full      - 全身彩色（去背景，保留原色）         → 图1
  color-head      - 头部彩色（去背景 + 裁切头部区域）     → 图2
  lineart-full    - 全身黑白线稿（印章风格线条）           → 图3
  lineart-head    - 头部黑白线稿（裁切头部 + 线稿）        → 图4

依赖:
  - remove.bg API（color-full / color-head 需要）
    在 stamp-customizer.js 的 init() 注入:
    window.StickerImageProcessor.REMOVEBG_API_KEY = '{{ config.removebg_api_key }}';
  - 不依赖 stamp-image-processor.js（独立运行）

在 stamp-ui.js 中路由（只需配置一次，之后增删风格只改本文件）:
  if (window.StickerImageProcessor.STYLES.includes(selectedPhotoStyle)) {
    processed = await window.StickerImageProcessor.process(img, selectedPhotoStyle);
  } else {
    // 走原有印章路由 ...
  }
==========================================
*/

window.StickerImageProcessor = (function () {
  'use strict';

  // remove.bg API Key，由外部注入
  var _removebgKey = '';

  // ─────────────────────────────────────────────────
  //  公开入口：统一路由
  //  新增风格时只在此 switch 加一行，stamp-ui.js 无需变动
  // ─────────────────────────────────────────────────
  async function process(img, style) {
    switch (style) {
      case 'color-full':   return _colorFull(img);
      case 'color-head':   return _colorHead(img);
      case 'lineart-full': return _lineartFull(img);
      case 'lineart-head': return _lineartHead(img);
      default:
        console.warn('[StickerImageProcessor] 未知风格:', style, '→ 降级为 color-full');
        return _colorFull(img);
    }
  }

  // ─────────────────────────────────────────────────
  //  Style: color-full — 全身彩色去背景（图1）
  //  原图 → remove.bg 去背 → 透明PNG，保留原色
  // ─────────────────────────────────────────────────
  async function _colorFull(img) {
    var dataUrl = _imgToDataUrl(img);
    return _removeBg(dataUrl);
  }

  // ─────────────────────────────────────────────────
  //  Style: color-head — 头部彩色去背景（图2）
  //  原图 → remove.bg 去背 → 裁切上 58%（头+肩）
  // ─────────────────────────────────────────────────
  async function _colorHead(img) {
    var dataUrl = _imgToDataUrl(img);
    var bgRemoved = await _removeBg(dataUrl);
    return _cropHead(bgRemoved, 0.58);
  }

  // ─────────────────────────────────────────────────
  //  Style: lineart-full — 全身黑白线稿（图3）
  //  原图 → 黑白线稿（参数与现有印章 line-art 一致）
  // ─────────────────────────────────────────────────
  function _lineartFull(img) {
    return _processLineArt(img, {
      windowSize: 11, threshVal: 155, offsetFactor: 0.15,
      hairThresh: 90, denoiseKeep: 2, denoiseFill: 7, smoothPasses: 1
    });
  }

  // ─────────────────────────────────────────────────
  //  Style: lineart-head — 头部黑白线稿（图4）
  //  原图 → 黑白线稿 → 裁切上 58%（头+肩）
  // ─────────────────────────────────────────────────
  async function _lineartHead(img) {
    var lineArt = await _processLineArt(img, {
      windowSize: 11, threshVal: 155, offsetFactor: 0.15,
      hairThresh: 90, denoiseKeep: 2, denoiseFill: 7, smoothPasses: 1
    });
    return _cropHead(lineArt, 0.58);
  }

  // ─────────────────────────────────────────────────
  //  内部: remove.bg API
  //  成功 → 透明PNG dataURL；失败 → 降级返回原dataUrl
  // ─────────────────────────────────────────────────
  async function _removeBg(dataUrl) {
    if (!_removebgKey || _removebgKey === 'YOUR_REMOVEBG_API_KEY') {
      console.warn('[StickerImageProcessor] remove.bg Key 未配置，跳过抠图');
      return dataUrl;
    }
    try {
      var blob = await fetch(dataUrl).then(function(r){ return r.blob(); });
      var fd = new FormData();
      fd.append('image_file', blob, 'photo.png');
      fd.append('size', 'auto');
      fd.append('channels', 'rgba');
      var res = await fetch('https://api.remove.bg/v1.0/removebg', {
        method: 'POST',
        headers: { 'X-Api-Key': _removebgKey },
        body: fd
      });
      if (!res.ok) {
        console.warn('[StickerImageProcessor] remove.bg 失败，降级:', await res.text().catch(function(){ return res.status; }));
        return dataUrl;
      }
      return _blobToDataUrl(await res.blob());
    } catch (err) {
      console.warn('[StickerImageProcessor] remove.bg 异常，降级:', err);
      return dataUrl;
    }
  }

  // ─────────────────────────────────────────────────
  //  内部: 头部裁切
  //  srcDataUrl : 任意 dataURL（支持透明PNG）
  //  headRatio  : 保留顶部比例，0.58 = 头部+肩膀
  // ─────────────────────────────────────────────────
  function _cropHead(srcDataUrl, headRatio) {
    headRatio = headRatio || 0.58;
    return new Promise(function(resolve, reject) {
      var img = new Image();
      img.onload = function() {
        var w = img.width;
        var h = Math.round(img.height * headRatio);
        var canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        var ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, w, h); // 保留透明通道
        ctx.drawImage(img, 0, 0, w, img.height, 0, 0, w, h);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = srcDataUrl;
    });
  }

  // ─────────────────────────────────────────────────
  //  内部: 黑白线稿核心
  //  算法与 stamp-image-processor.js 的 _processLineArt 完全一致
  //  独立实现，不依赖外部文件
  // ─────────────────────────────────────────────────
  function _processLineArt(img, options) {
    return new Promise(function(resolve, reject) {
      try {
        var windowSize   = options.windowSize   !== undefined ? options.windowSize   : 11;
        var threshVal    = options.threshVal    !== undefined ? options.threshVal    : 155;
        var offsetFactor = options.offsetFactor !== undefined ? options.offsetFactor : 0.15;
        var hairThresh   = options.hairThresh   !== undefined ? options.hairThresh   : 90;
        var denoiseKeep  = options.denoiseKeep  !== undefined ? options.denoiseKeep  : 2;
        var denoiseFill  = options.denoiseFill  !== undefined ? options.denoiseFill  : 7;
        var smoothPasses = options.smoothPasses !== undefined ? options.smoothPasses : 1;
        var maxSize      = options.maxSize      || 1800;
        var contrast     = options.contrast     || '100%';
        var blur         = options.blur         || '0.5px';

        // 缩放
        var canvas = document.createElement('canvas');
        var ctx = canvas.getContext('2d');
        var W = img.width, H = img.height;
        if (W > H) { if (W > maxSize) { H = Math.round(H * maxSize / W); W = maxSize; } }
        else       { if (H > maxSize) { W = Math.round(W * maxSize / H); H = maxSize; } }
        canvas.width = W; canvas.height = H;

        // 白底 + 预处理
        var tc = document.createElement('canvas');
        tc.width = W; tc.height = H;
        var tctx = tc.getContext('2d');
        tctx.fillStyle = '#ffffff';
        tctx.fillRect(0, 0, W, H);
        tctx.drawImage(img, 0, 0, W, H);
        tctx.filter = 'contrast(' + contrast + ') blur(' + blur + ')';
        tctx.drawImage(tc, 0, 0);
        tctx.filter = 'none';

        var id = tctx.getImageData(0, 0, W, H);
        var d  = new Uint8ClampedArray(id.data);

        // 灰度
        for (var i = 0; i < d.length; i += 4) {
          var g = d[i]*0.299 + d[i+1]*0.587 + d[i+2]*0.114;
          d[i] = d[i+1] = d[i+2] = g;
        }

        // 自适应阈值（轮廓线层）
        var sk = new Uint8ClampedArray(d.length);
        for (var y = 0; y < H; y++) {
          for (var x = 0; x < W; x++) {
            var idx = (y*W+x)*4, sum = 0, cnt = 0;
            for (var dy = -windowSize; dy <= windowSize; dy++) {
              for (var dx = -windowSize; dx <= windowSize; dx++) {
                var ny = y+dy, nx = x+dx;
                if (ny>=0 && ny<H && nx>=0 && nx<W) { sum += d[(ny*W+nx)*4]; cnt++; }
              }
            }
            var mean = sum / cnt;
            var off  = (255 - threshVal) * offsetFactor;
            var v = d[idx] < (mean - off) ? 0 : 255;
            sk[idx] = sk[idx+1] = sk[idx+2] = v; sk[idx+3] = 255;
          }
        }

        // 深色填充层（头发）
        var hl = new Uint8ClampedArray(d.length); hl.fill(255);
        for (var i2 = 0; i2 < d.length; i2+=4) {
          if (d[i2] < hairThresh) hl[i2] = hl[i2+1] = hl[i2+2] = 0;
          hl[i2+3] = 255;
        }
        var th = new Uint8ClampedArray(hl);
        for (var y2=1; y2<H-1; y2++) {
          for (var x2=1; x2<W-1; x2++) {
            var i3=(y2*W+x2)*4;
            if (th[i3]===255) {
              var hb=false;
              for (var dy2=-1; dy2<=1&&!hb; dy2++)
                for (var dx2=-1; dx2<=1&&!hb; dx2++)
                  if (th[((y2+dy2)*W+(x2+dx2))*4]===0) hb=true;
              if (hb) hl[i3]=hl[i3+1]=hl[i3+2]=0;
            }
          }
        }

        // 合并
        var res = new Uint8ClampedArray(d.length);
        for (var i4=0; i4<d.length; i4+=4) {
          var val = (sk[i4]===0 || hl[i4]===0) ? 0 : 255;
          res[i4]=res[i4+1]=res[i4+2]=val; res[i4+3]=255;
        }

        // 降噪（2轮）
        for (var it=0; it<2; it++) {
          var tmp = new Uint8ClampedArray(res);
          for (var y3=1; y3<H-1; y3++) {
            for (var x3=1; x3<W-1; x3++) {
              var i5=(y3*W+x3)*4, bc=0;
              for (var dy3=-1; dy3<=1; dy3++)
                for (var dx3=-1; dx3<=1; dx3++)
                  if (tmp[((y3+dy3)*W+(x3+dx3))*4]===0) bc++;
              if (bc<=denoiseKeep) res[i5]=res[i5+1]=res[i5+2]=255;
              else if (bc>=denoiseFill) res[i5]=res[i5+1]=res[i5+2]=0;
            }
          }
        }

        // 边缘平滑
        for (var p=0; p<smoothPasses; p++) {
          var sm = new Uint8ClampedArray(res);
          for (var y4=1; y4<H-1; y4++) {
            for (var x4=1; x4<W-1; x4++) {
              var i6=(y4*W+x4)*4, hbk=false;
              for (var dy4=-1; dy4<=1&&!hbk; dy4++)
                for (var dx4=-1; dx4<=1&&!hbk; dx4++)
                  if (res[((y4+dy4)*W+(x4+dx4))*4]===0) hbk=true;
              if (hbk) sm[i6]=sm[i6+1]=sm[i6+2]=0;
            }
          }
          var fn2 = new Uint8ClampedArray(sm);
          for (var y5=1; y5<H-1; y5++) {
            for (var x5=1; x5<W-1; x5++) {
              var i7=(y5*W+x5)*4, ab=true;
              for (var dy5=-1; dy5<=1&&ab; dy5++)
                for (var dx5=-1; dx5<=1&&ab; dx5++)
                  if (sm[((y5+dy5)*W+(x5+dx5))*4]===255) ab=false;
              if (!ab) fn2[i7]=fn2[i7+1]=fn2[i7+2]=255;
            }
          }
          res = fn2;
        }

        id.data.set(res);
        ctx.putImageData(id, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch(err) { reject(err); }
    });
  }

  // ─────────────────────────────────────────────────
  //  工具函数
  // ─────────────────────────────────────────────────
  function _imgToDataUrl(img) {
    var c = document.createElement('canvas');
    c.width = img.width; c.height = img.height;
    c.getContext('2d').drawImage(img, 0, 0);
    return c.toDataURL('image/png');
  }

  function _blobToDataUrl(blob) {
    return new Promise(function(resolve, reject) {
      var r = new FileReader();
      r.onloadend = function(){ resolve(r.result); };
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  }

  // ─────────────────────────────────────────────────
  //  公开 API
  // ─────────────────────────────────────────────────
  return {
    // API Key 注入（由 stamp-customizer.js 调用）
    set REMOVEBG_API_KEY(k) { _removebgKey = k; },
    get REMOVEBG_API_KEY()  { return _removebgKey; },

    // 统一入口（推荐，新增风格只改 process() 的 switch）
    process: process,

    // 单独调用（备用）
    convertToColorFull:   _colorFull,
    convertToColorHead:   _colorHead,
    convertToLineartFull: _lineartFull,
    convertToLineartHead: _lineartHead,

    // 风格列表（stamp-ui.js 用来判断是否走贴纸路由）
    STYLES: ['color-full', 'color-head', 'lineart-full', 'lineart-head']
  };

})();

if (typeof module !== 'undefined' && module.exports) {
  module.exports = window.StickerImageProcessor;
}